import AuditService from './auditService'
import * as Sentry from "@sentry/react"

// Types réutilisés depuis subscriptionService
export interface SubscriptionResponse {
  status: 'active' | 'expired' | 'suspended' | 'cancelled' | 'trial'
  expiryDate?: string
  plan: string
  price: number
  nextBilling?: string
  daysUntilNext?: number
  statusMessage?: string
  trialEndDate?: string
  hoursRemaining?: number
}

export interface PaymentResult {
  success: boolean
  data?: {
    orderId: string
    formToken?: string        // Lyra / SystemPay (Krypton)
    amount?: number
    provider?: string         // 'authorizenet' pour Accept Hosted
    token?: string            // Authorize.net : token de page hébergée
    formUrl?: string          // Authorize.net : URL de POST de la page hébergée
    mode?: string
  }
  message?: string
}

export interface TrialSubscriptionResponse {
  success: boolean
  data?: {
    orderId: string
    formToken: string
    publicKey: string
    mode: string
    trialEndDate: string
    subscriptionId: string
    amount: number
  }
  message?: string
}

export interface TrialStatusResponse {
  success: boolean
  data?: {
    status: 'trial' | 'active' | 'expired' | 'cancelled'
    trialEndDate?: string
    hoursRemaining?: number
    subscriptionId?: string
  }
  message?: string
}

export class SubscriptionServiceZxsem {
  // App de paiement centrale (mêmes fonctions Netlify ; domaine canonique)
  private baseUrl: string = 'https://systempaymenttahiti.netlify.app/.netlify/functions'
  // Clé API du site parks-avenue.com (Authorize.net)
  private apiKey: string = 'sk_live_244979b47b588655ba277023457f2a7cc7cce101dacd10c4963dba805d846f4e'

  // Authorize.net facture en USD (devise du compte marchand) → plus de conversion XPF.
  // Montants en centimes USD.
  private getPlanConfigByCode(planCode: string) {
    if (planCode === 'weekly') {
      return { planName: 'Parking Unlimited Weekly', frequency: 'weekly', amount: 1499 }
    }
    if (planCode === 'payg') {
      return { planName: 'Parking Pay As You Go', frequency: 'one-time', amount: 700 }
    }
    return { planName: 'Parking Unlimited Monthly', frequency: 'monthly', amount: 4999 }
  }

  // URL absolue du communicator Accept Hosted (hébergé sur ce domaine)
  private iframeCommunicatorUrl(): string {
    return (typeof window !== 'undefined' ? window.location.origin : '') + '/authorizenet/IFrameCommunicator.html'
  }

  // Config CLIENT Authorize.net pour Accept.js (apiLoginID + Public Client Key + URL lib) — valeurs publiques
  async getAuthNetClientConfig(): Promise<{ apiLoginId: string; publicClientKey: string; mode: string; acceptJsUrl: string } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/authnet-client-config`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` }
      })
      const json = await res.json()
      return json.success ? json.data : null
    } catch (error) {
      console.error('❌ [Z] getAuthNetClientConfig:', error)
      return null
    }
  }

  // Config CLIENT NMI pour Collect.js (tokenisation) + Gateway.js (3DS 2.0) — valeurs publiques
  async getNmiClientConfig(): Promise<{ tokenizationKey: string; mode: string; gatewayBase: string; collectJsUrl: string; gatewayJsUrl: string } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/nmi-client-config`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` }
      })
      const json = await res.json()
      return json.success ? json.data : null
    } catch (error) {
      console.error('❌ [Z] getNmiClientConfig:', error)
      return null
    }
  }

  async checkSubscriptionStatus(email: string): Promise<SubscriptionResponse | null> {
    try {
      console.log('🔍 [Z] Vérification du statut d\'abonnement pour:', email)

      const response = await fetch(`${this.baseUrl}/get-subscription-status?customerEmail=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      console.log('📋 [Z] Réponse API statut:', result)

      if (result.success && result.data && result.data.length > 0) {
        const activeSubscription = result.data.find((sub: any) => sub.status === 'active')

        if (activeSubscription) {
          return {
            status: 'active',
            expiryDate: activeSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            nextBilling: activeSubscription.nextPaymentDate,
            daysUntilNext: activeSubscription.daysUntilNextPayment,
            statusMessage: activeSubscription.statusMessage
          }
        }

        const trialSubscription = result.data.find((sub: any) => sub.status === 'trial')

        if (trialSubscription) {
          return {
            status: 'trial',
            expiryDate: trialSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            nextBilling: trialSubscription.nextPaymentDate,
            daysUntilNext: trialSubscription.daysUntilNextPayment,
            statusMessage: `Trial period - ${trialSubscription.daysUntilNextPayment} day(s) remaining`,
            trialEndDate: trialSubscription.nextPaymentDate
          }
        }

        const otherSubscription = result.data[0]
        if (otherSubscription) {
          return {
            status: otherSubscription.status === 'cancelled' ? 'cancelled' : 'expired',
            expiryDate: otherSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            statusMessage: otherSubscription.statusMessage
          }
        }
      }

      return {
        status: 'expired',
        plan: 'monthly',
        price: 49.99
      }

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la vérification du statut:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionServiceZxsem',
          method: 'checkSubscriptionStatus'
        },
        extra: {
          email,
          baseUrl: this.baseUrl
        }
      })
      return null
    }
  }

  async createSubscriptionWithPayment(
    email: string,
    userId?: string,
    options?: {
      planCode?: string
      planName?: string
      subscriptionAmount?: number
      initialAmount?: number
      frequency?: string
      opaqueData?: { dataDescriptor: string; dataValue: string }  // nonce Accept.js (Authorize.net)
      paymentToken?: string        // NMI : token Collect.js
      threeDS?: Record<string, any> // NMI : résultat Gateway.js 3DS 2.0 (cavv/eci/xid/…)
      bin?: string                 // NMI : BIN carte (Collect.js)
      customerName?: string        // NMI : nom porteur (3DS)
    }
  ): Promise<PaymentResult> {
    try {
      console.log('🚀 [Z] Création d\'abonnement avec paiement pour:', email)

      if (userId) {
        await AuditService.logUserActivity({
          userId,
          eventType: 'PAYMENT_INITIATED',
          details: { email, method: 'subscription_with_payment_z' }
        })
      }

      const planCode = options?.planCode || 'monthly'
      const defaultPlan = this.getPlanConfigByCode(planCode)
      const planName = options?.planName || defaultPlan.planName
      const frequency = options?.frequency || defaultPlan.frequency
      // Montants en centimes USD (aucune conversion — compte Authorize.net en USD)
      const subscriptionAmount = options?.subscriptionAmount || defaultPlan.amount
      const initialAmount = options?.initialAmount || subscriptionAmount

      // Authorize.net : un seul endpoint (subscription_init = débit initial + profil CIM rebill).
      // Le webhook crée l'abonnement après confirmation.
      const subscriptionData = {
        customerEmail: email,
        planName,
        subscriptionAmount,
        initialAmount,
        frequency,
        currency: 'USD',
        customerReference: `parking_${email}_${Date.now()}`,
        iframeCommunicatorUrl: this.iframeCommunicatorUrl(),
        ...(options?.opaqueData ? { opaqueData: options.opaqueData } : {}),  // Accept.js : charge synchrone du nonce
        // NMI : token Collect.js + résultat 3DS 2.0 + BIN (charge CIT synchrone côté backend)
        ...(options?.paymentToken ? { paymentToken: options.paymentToken } : {}),
        ...(options?.threeDS ? { threeDS: options.threeDS } : {}),
        ...(options?.bin ? { bin: options.bin } : {}),
        ...(options?.customerName ? { customer: { name: options.customerName } } : {})
      }

      const response = await fetch(`${this.baseUrl}/create-subscription-with-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` },
        body: JSON.stringify(subscriptionData)
      })
      const result = await response.json()
      console.log('📤 [Z] Réponse création abonnement (Authorize.net):', result)

      if (userId && result.success && result.data) {
        await AuditService.logPaymentAudit({
          userId,
          paymentId: result.data.orderId,
          amount: initialAmount / 100,
          currency: 'USD',
          paymentStatus: 'initiated'
        })
      }

      return result

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la création de l\'abonnement:', error)

      if (userId) {
        await AuditService.logUserActivity({
          userId,
          eventType: 'PAYMENT_ERROR',
          details: {
            email,
            error: error instanceof Error ? error.message : 'Unknown error',
            method: 'subscription_with_payment_z'
          }
        })
      }

      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionServiceZxsem',
          method: 'createSubscriptionWithPayment'
        },
        extra: {
          email,
          userId,
          baseUrl: this.baseUrl
        }
      })

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      }
    }
  }

  async getSubscriptionId(customerEmail: string): Promise<{ success: boolean; subscriptionId?: string; message?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/get-subscription-status?customerEmail=${encodeURIComponent(customerEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()

      if (result.success && result.data && result.data.length > 0) {
        const activeSubscription = result.data.find((sub: any) => sub.status === 'active')
        if (activeSubscription && activeSubscription.id) {
          return { success: true, subscriptionId: activeSubscription.id }
        }

        const trialSubscription = result.data.find((sub: any) => sub.status === 'trial')
        if (trialSubscription && trialSubscription.id) {
          return { success: true, subscriptionId: trialSubscription.id }
        }

        const firstSubscription = result.data[0]
        if (firstSubscription && firstSubscription.id) {
          return { success: true, subscriptionId: firstSubscription.id }
        }
      }

      return { success: false, message: 'Aucun abonnement trouvé pour cet email' }

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la récupération de l\'ID:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZxsem', method: 'getSubscriptionId' },
        extra: { customerEmail, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' }
    }
  }

  async cancelSubscription(subscriptionIdOrEmail: string, isEmail: boolean = false, reason: string = 'Annulation demandée par l\'utilisateur'): Promise<{ success: boolean; message?: string }> {
    try {
      let subscriptionId = subscriptionIdOrEmail

      if (isEmail) {
        const idResult = await this.getSubscriptionId(subscriptionIdOrEmail)
        if (!idResult.success || !idResult.subscriptionId) {
          return { success: false, message: idResult.message || 'Impossible de récupérer l\'ID de l\'abonnement' }
        }
        subscriptionId = idResult.subscriptionId
      }

      const response = await fetch(`${this.baseUrl}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          reason: reason,
          calculateRefund: false,
          immediateTermination: false
        })
      })

      const result = await response.json()

      if (result.success) {
        return { success: true, message: 'Abonnement annulé avec succès' }
      } else {
        return { success: false, message: result.message || 'Erreur lors de l\'annulation' }
      }

    } catch (error) {
      console.error('❌ [Z] Erreur lors de l\'annulation:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZxsem', method: 'cancelSubscription' },
        extra: { subscriptionIdOrEmail, isEmail, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' }
    }
  }

  async getTransactionStatus(orderId: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/get-transaction-status?orderId=${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      return result

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la vérification du statut de transaction:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZxsem', method: 'getTransactionStatus' },
        extra: { orderId, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' }
    }
  }

  async createPaidTrialFromPayg(
    email: string,
    trialAmount: number,
    userId?: string,
    fullName?: string
  ): Promise<TrialSubscriptionResponse> {
    try {
      console.log('🚀 [Z] Creating paid trial from PAYG for:', email)

      // USD (compte Authorize.net) — pas de conversion
      const isMonthly = trialAmount >= 4000
      const frequency = isMonthly ? 'monthly' : 'weekly'
      const trialDays = isMonthly ? 30 : 7
      const trialDurationHours = trialDays * 24
      const planName = isMonthly ? 'Parking Pay As You Go Monthly' : 'Parking Pay As You Go Weekly'

      console.log(`📋 [Zxsem] Payg → abo ${frequency}, montant USD: ${trialAmount}, essai: ${trialDays}j (${trialDurationHours}h)`)

      const requestData = {
        customerEmail: email,
        planName,
        subscriptionAmount: trialAmount,
        frequency,
        trialDurationHours,
        trialAmount: trialAmount,
        currency: 'USD',
        iframeCommunicatorUrl: this.iframeCommunicatorUrl(),
        customer: {
          name: fullName || 'Premium User',
          phone: '+1 000 000 0000'
        }
      }

      const response = await fetch(`${this.baseUrl}/create-subscription-with-trial-v3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 [Z] Paid trial from PAYG response:', result)

      return result

    } catch (error) {
      console.error('❌ [Z] Error creating paid trial from PAYG:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZxsem', method: 'createPaidTrialFromPayg' },
        extra: { email, trialAmount, userId, fullName, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Unknown error' }
    }
  }
}

export const subscriptionServiceZxsem = new SubscriptionServiceZxsem()
